<template>
  <div class="card h-100">
    <div class="card-header">
      <div class="header-icon">
        <i class="fa-solid fa-brain"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Category Training</h3>
        <p class="card-subtitle">Teach auto-categorization with keywords</p>
      </div>
    </div>

    <div class="card-body ps-3 pe-3 pb-3">
      <!-- Tab Navigation -->
      <div class="tab-navigation nested-container mb-3">
        <button 
          @click="activeTab = 'keywords'" 
          class="tab-button"
          :class="{ active: activeTab === 'keywords' }"
        >
          <i class="fa-solid fa-list me-1"></i>Keywords
        </button>
        <button 
          @click="activeTab = 'ignored'" 
          class="tab-button"
          :class="{ active: activeTab === 'ignored' }"
        >
          <i class="fa-solid fa-ban me-1"></i>Ignored
        </button>
      </div>

      <!-- Keywords Tab -->
      <div v-show="activeTab === 'keywords'">
        <!-- Add New Keyword -->
        <div class="add-keyword-section nested-container mb-3">
          <label class="form-label fw-semibold">
            <i class="fa-solid fa-key me-1"></i>Keyword
          </label>
          <div class="input-group mb-2">
            <input
              v-model="newKeyword"
              type="text"
              class="form-control form-control-lg"
              placeholder="e.g., plants"
              @keyup.enter="addKeyword"
            />
          </div>
          <label class="form-label fw-semibold">
            <i class="fa-solid fa-tag me-1"></i>Category
          </label>
          <div class="input-group mb-2">
            <input
              v-model="newCategory"
              type="text"
              class="form-control form-control-lg"
              placeholder="e.g., Shopping"
              @keyup.enter="addKeyword"
            />
          </div>
          <button @click="addKeyword" class="btn btn-primary w-100 mt-2" :disabled="isLoading">
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i v-else class="fa-solid fa-plus me-1"></i>Add Rule
          </button>
        </div>

        <!-- Keyword List -->
        <div class="keywords-section">
          <!-- Loading State -->
          <div v-if="isLoading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            Loading keywords...
          </div>
          
          <!-- Error State -->
          <div v-else-if="error" class="text-danger text-center py-3">
            <i class="fa-solid fa-exclamation-triangle me-1"></i>{{ error }}
          </div>
          
          <!-- Empty State -->
          <div v-else-if="keywordCount === 0" class="text-muted text-center py-3">
            <i class="fa-solid fa-circle-info me-1"></i>No keyword rules yet
          </div>
          
          <!-- Keywords List with Collapsible Header -->
          <div v-else class="keywords-list-section">
            <!-- Expandable Header -->
            <button 
              @click="showKeywords = !showKeywords"
              class="expand-button nested-container w-100 d-flex justify-content-between align-items-center"
            >
              <label class="form-label fw-semibold mb-0">
                <i class="fa-solid fa-list me-1"></i>Active Rules ({{ keywordCount }})
              </label>
              <i class="fa-solid" :class="showKeywords ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>

            <!-- Expandable Content -->
            <div v-show="showKeywords" class="keyword-items mt-2">
              <div
                v-for="mapping in keywords"
                :key="mapping.keyword"
                class="keyword-item nested-container"
              >
                <div class="keyword-info">
                  <span class="keyword-text">{{ mapping.keyword }}</span>
                  <i class="fa-solid fa-arrow-right mx-2"></i>
                  <span class="category-badge">{{ mapping.category }}</span>
                </div>
                <button
                  @click="removeKeyword(mapping.keyword)"
                  class="btn delete-btn"
                  :disabled="isLoading"
                >
                  <i class="bi bi-trash-fill"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Ignored Words Tab -->
      <div v-show="activeTab === 'ignored'">
        <!-- Add Ignored Word -->
        <div class="add-keyword-section nested-container mb-3">
          <label class="form-label fw-semibold">
            <i class="fa-solid fa-ban me-1"></i>Word to Ignore
          </label>
          <div class="input-group mb-2">
            <input
              v-model="newIgnoredWord"
              type="text"
              class="form-control form-control-lg"
              placeholder="e.g., bought, purchased"
              @keyup.enter="addIgnored"
            />
          </div>
          <button @click="addIgnored" class="btn btn-primary w-100 mt-2">
            <i class="fa-solid fa-plus me-1"></i>Add to Ignore List
          </button>
        </div>

        <!-- Ignored Words List -->
        <div class="ignored-words-section">
          <!-- Empty State -->
          <div v-if="ignoredWordsCount === 0" class="text-muted text-center py-3">
            <i class="fa-solid fa-circle-info me-1"></i>No ignored words yet
          </div>
          
          <!-- Ignored Words List with Collapsible Header -->
          <div v-else class="ignored-words-list-section">
            <!-- Expandable Header -->
            <button 
              @click="showIgnoredWords = !showIgnoredWords"
              class="expand-button nested-container w-100 d-flex justify-content-between align-items-center"
            >
              <label class="form-label fw-semibold mb-0">
                <i class="fa-solid fa-ban me-1"></i>Ignored Words ({{ ignoredWordsCount }})
              </label>
              <i class="fa-solid" :class="showIgnoredWords ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>

            <!-- Expandable Content -->
            <div v-show="showIgnoredWords" class="keyword-items mt-2">
              <div
                v-for="word in ignoredWords"
                :key="word"
                class="keyword-item nested-container ignored-item"
              >
                <div class="keyword-info">
                  <span class="keyword-text">{{ word }}</span>
                </div>
                <button
                  @click="removeIgnored(word)"
                  class="btn delete-btn"
                >
                  <i class="bi bi-trash-fill"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useCategoryKeywords } from '../composables/useCategoryKeywords';
import { useAuth } from '../composables/useAuth';

export default {
  name: 'CategoryKeywords',
  props: {
    user: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    const { getAccessToken } = useAuth();
    const { 
      categoryKeywords: keywords, 
      ignoredWords,
      keywordCount, 
      ignoredWordsCount,
      isLoading,
      error,
      loadKeywords,
      addKeyword: addKeywordToStore, 
      removeKeyword: removeKeywordFromStore,
      addIgnoredWord: addIgnoredWordToStore,
      removeIgnoredWord: removeIgnoredWordFromStore
    } = useCategoryKeywords();
    
    const activeTab = ref('keywords');
    const newKeyword = ref('');
    const newCategory = ref('');
    const newIgnoredWord = ref('');
    const showKeywords = ref(false);
    const showIgnoredWords = ref(false);

    // Load keywords when component mounts and user is authenticated
    onMounted(async () => {
      if (props.user) {
        try {
          const accessToken = await getAccessToken();
          await loadKeywords(accessToken);
        } catch (error) {
          console.error('Failed to load keywords on mount:', error);
        }
      }
    });

    // Watch for user authentication changes
    watch(() => props.user, async (newUser) => {
      if (newUser) {
        try {
          const accessToken = await getAccessToken();
          await loadKeywords(accessToken);
        } catch (error) {
          console.error('Failed to load keywords after auth:', error);
        }
      }
    });

    const addKeyword = async () => {
      const keyword = newKeyword.value.trim();
      const category = newCategory.value.trim();

      if (!keyword || !category || !props.user) {
        return;
      }

      try {
        const accessToken = await getAccessToken();
        const success = await addKeywordToStore(keyword, category, accessToken);
        
        if (success) {
          newKeyword.value = '';
          newCategory.value = '';
        }
      } catch (error) {
        console.error('Failed to add keyword:', error);
      }
    };

    const removeKeyword = async (keyword) => {
      if (!props.user) return;

      try {
        const accessToken = await getAccessToken();
        await removeKeywordFromStore(keyword, accessToken);
      } catch (error) {
        console.error('Failed to remove keyword:', error);
      }
    };

    const addIgnored = () => {
      const word = newIgnoredWord.value.trim();

      if (!word) {
        return;
      }

      addIgnoredWordToStore(word);
      newIgnoredWord.value = '';
    };

    const removeIgnored = (word) => {
      removeIgnoredWordFromStore(word);
    };

    return {
      activeTab,
      keywords,
      ignoredWords,
      keywordCount,
      ignoredWordsCount,
      isLoading,
      error,
      newKeyword,
      newCategory,
      newIgnoredWord,
      showKeywords,
      showIgnoredWords,
      addKeyword,
      removeKeyword,
      addIgnored,
      removeIgnored
    };
  }
};
</script>

<style scoped>
.card {
  border-radius: 5px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--light-border);
  margin-bottom: 1rem;
}

.header-content {
  flex: 1;
  min-width: 0;
}

.card-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--light-text);
  line-height: 1.2;
}

.card-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: var(--light-text-muted);
  line-height: 1.3;
}

.card-body {
  padding: 0;
}

.btn-primary {
  background-color: #36A2EB;
  border-color: #36A2EB;
  min-height: 44px;
}

.btn-primary:hover {
  background-color: #2a8fd4;
  border-color: #2a8fd4;
}

.add-keyword-section {
  /* Additional styling beyond nested-container */
}

.expand-button {
  /* Additional styling beyond nested-container */
  border: none;
  cursor: pointer;
  color: var(--light-text);
}

.expand-button .form-label {
  cursor: pointer;
  color: var(--light-text);
}

.keyword-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.keyword-item {
  /* Additional styling beyond nested-container */
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.keyword-info {
  display: flex;
  align-items: center;
  flex: 1;
  font-size: 14px;
}

.keyword-text {
  font-weight: 600;
  color: var(--light-text);
}

.category-badge {
  padding: 4px 12px;
  background: var(--light-text);
  color: var(--light-card);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.tab-navigation {
  /* Additional styling beyond nested-container */
  display: flex;
  gap: 8px;
}

.tab-button {
  flex: 1;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--light-text-muted);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-button:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--light-text);
}

.tab-button.active {
  background: var(--light-text);
  color: var(--light-card);
}

.ignored-item {
  border-left: 3px solid var(--light-danger);
}

/* Dark mode */
:global([data-theme]) .card-title {
  color: var(--light-text);
}

:global([data-theme]) .card-subtitle {
  color: var(--light-text-muted);
}

:global([data-theme]) .keyword-text {
  color: var(--light-text);
}

:global([data-theme]) .tab-button:hover {
  background: rgba(255, 255, 255, 0.08);
}

:global([data-theme]) .tab-button.active {
  background: var(--light-text);
  color: var(--light-bg);
}

:global([data-theme]) .expand-button {
  color: var(--light-text);
}

:global([data-theme]) .expand-button .form-label {
  color: var(--light-text);
}

@media (max-width: 768px) {
  .card-title {
    font-size: 1rem;
  }

  .card-subtitle {
    font-size: 0.8rem;
  }

  .keyword-info {
    font-size: 13px;
  }
}
</style>
